import { Placement } from '@popperjs/core'
import { Tooltip } from '@positivote/design-system/components/Tooltip'
import { useTheme } from '@positivote/design-system/hooks'
import { AlertIcon } from '@positivote/design-system/icons/Alert'
import { CheckCircleIcon } from '@positivote/design-system/icons/CheckCircle'
import { Breakpoint } from '@positivote/design-system/theme'
import { useEffect } from 'react'
import { isDesktop, isMobile, isTablet } from 'react-device-detect'

import { i18n } from '@/common/i18n'

import { AccordionRequirements } from './AccordionRequirements'

export interface AccordionHardwareProps {
  isLoading: boolean
  onChangeHardwareInfos: (infos: {
    screenSize: string
    isTouchScreen: string
    deviceType: string
  }) => void
}

export function AccordionHardware({
  isLoading,
  onChangeHardwareInfos
}: AccordionHardwareProps): JSX.Element {
  const screenWidth = window.screen.width
  const screenHeight = window.screen.height
  const maxScreenWidth = 1920
  const maxScreenHeight = 1080
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0

  const { breakpoint } = useTheme()

  const tooltipPlacement: Placement = breakpoint >= Breakpoint.lg ? 'right' : 'left'

  function showDeviceType(): string {
    let deviceType = ''
    if (isMobile) {
      deviceType = 'Mobile'
    }
    if (isTablet) {
      deviceType = 'Tablet'
    }
    if (isDesktop) {
      deviceType = 'Desktop'
    }
    return deviceType
  }

  useEffect(() => {
    onChangeHardwareInfos({
      screenSize: `${screenWidth}x${screenHeight}`,
      isTouchScreen: isTouchDevice
        ? i18n().modules.requirements.components.accordionHardware.yes
        : i18n().modules.requirements.components.accordionHardware.no,
      deviceType: showDeviceType()
    })
    // DOCS: This useeffect can only be used when changing states
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <AccordionRequirements
      requirementsProps={{
        title: i18n().modules.requirements.components.accordionHardware.title,
        descriptions: [
          {
            key: 'screenSize',
            label: i18n().modules.requirements.components.accordionHardware.screenSize,
            value: `${screenWidth}x${screenHeight}`,
            icon:
              screenWidth <= maxScreenWidth && screenHeight <= maxScreenHeight ? (
                <CheckCircleIcon fill="$success" />
              ) : (
                <Tooltip
                  label={i18n().modules.requirements.components.accordionHardware.tooltip.maxScreen}
                  placement={tooltipPlacement}
                  arrow
                >
                  <AlertIcon fill="$warning" />
                </Tooltip>
              )
          },
          {
            key: 'touchScreen',
            label: i18n().modules.requirements.components.accordionHardware.touch,
            value: isTouchDevice
              ? i18n().modules.requirements.components.accordionHardware.yes
              : i18n().modules.requirements.components.accordionHardware.no,
            icon: <CheckCircleIcon fill="$success" />
          },
          {
            key: 'deviceType',
            label: i18n().modules.requirements.components.accordionHardware.deviceType,
            value: showDeviceType(),
            icon: <CheckCircleIcon fill="$success" />
          }
        ]
      }}
      isLoading={isLoading}
    />
  )
}
